import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { FileMetadataService, DocumentType, ProcessingStatus } from '../../../../shared/file-metadata.service';

/**
 * Dialog component for displaying detailed file metadata properties.
 * Shows comprehensive information from InputFileMetadata proto.
 */
@Component({
  selector: 'app-file-properties-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatChipsModule,
    MatDividerModule,
  ],
  templateUrl: './file-properties-dialog.component.html',
  styleUrls: ['./file-properties-dialog.component.scss'],
})
export class FilePropertiesDialogComponent {
  dialogRef = inject<MatDialogRef<FilePropertiesDialogComponent>>(MatDialogRef);
  data = inject<{
    fileMetadata: any;
  }>(MAT_DIALOG_DATA);
  fileMetadataService = inject(FileMetadataService);

  // Access enum types in template
  DocumentType = DocumentType;
  ProcessingStatus = ProcessingStatus;

  constructor() {
    const data = this.data;

    ClientLoggingService.log('INFO', 'FilePropertiesDialog opened with metadata:', data.fileMetadata);
  }

  /**
   * Gets the file metadata object from dialog data.
   */
  get metadata(): any {
    return this.data.fileMetadata;
  }

  /**
   * Formats file size in human-readable format.
   */
  formatFileSize(bytes: number): string {
    return this.fileMetadataService.formatFileSize(bytes);
  }

  /**
   * Formats document type for display.
   */
  formatDocumentType(documentType: DocumentType): string {
    return this.fileMetadataService.formatDocumentType(documentType);
  }

  /**
   * Formats processing status for display.
   */
  formatProcessingStatus(status: ProcessingStatus): string {
    return this.fileMetadataService.formatProcessingStatus(status);
  }

  /**
   * Gets status color class for UI styling.
   */
  getStatusColorClass(status: ProcessingStatus): string {
    return this.fileMetadataService.getStatusColorClass(status);
  }

  /**
   * Gets status icon based on processing status.
   */
  getStatusIcon(status: ProcessingStatus): string {
    switch (status) {
      case ProcessingStatus.PROCESSING_STATUS_UPLOADED:
        return 'cloud_upload';
      case ProcessingStatus.PROCESSING_STATUS_PROCESSING:
        return 'hourglass_empty';
      case ProcessingStatus.PROCESSING_STATUS_COMPLETED:
        return 'check_circle';
      case ProcessingStatus.PROCESSING_STATUS_FAILED:
        return 'error';
      default:
        return 'help';
    }
  }

  /**
   * Copies text to clipboard.
   */
  copyToClipboard(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        ClientLoggingService.log('INFO', 'Copied to clipboard:', text);
      })
      .catch(err => {
        ClientLoggingService.log('ERROR', 'Failed to copy:', err);
      });
  }

  /**
   * Closes the dialog.
   */
  close(): void {
    this.dialogRef.close();
  }
}
